// src/components/AuthStage.js
//
// Full-bleed flesh corridor (procedural Three.js) with the watching eye, and a
// centered card on top. Children receive `eye` to open/close the lid:
// eye.focusName() widens it, eye.focusSecret() shuts it, eye.blur() resets.

import React, { useMemo, useRef } from 'react';
import styled from 'styled-components';
import SceneCanvas from '../scene/SceneCanvas';
import { CornerCard } from './ui';
import { color, font, halftone } from '../theme/tokens';

const Stage = styled.div`
    position: relative;
    min-height: 100%;
    overflow: hidden;
    background: ${color.void};
    display: flex;
    justify-content: center;
    align-items: flex-start;
`;

const Layer = styled.div`
    position: fixed;
    inset: 0;
    pointer-events: none;
`;

const Halftone = styled(Layer)`
    z-index: 2;
    opacity: 0.32;
    ${halftone}
`;

const Vignette = styled(Layer)`
    z-index: 2;
    background:
        linear-gradient(180deg, rgba(7, 2, 5, 0.55) 0%, transparent 16%, transparent 80%, rgba(7, 2, 5, 0.75) 100%),
        radial-gradient(ellipse at 50% 45%, transparent 45%, rgba(7, 2, 5, 0.7) 100%);
`;

const Column = styled.div`
    position: relative;
    z-index: 3;
    box-sizing: border-box;
    width: 100%;
    max-width: 448px;
    padding: max(30vh, 240px) 24px 120px;
    @media (max-height: 760px) { padding-top: 200px; }
    @media (max-width: 640px) { padding: 180px 16px 120px; }
`;

const Head = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    margin-bottom: 28px;
    h1 {
        margin: 0;
        font-family: ${font.display};
        font-weight: 800;
        font-size: 34px;
        letter-spacing: 0.4em;
        padding-left: 0.4em;
        color: ${color.textBright};
    }
    p {
        margin: 0;
        text-align: center;
        font-family: ${font.display};
        font-size: 14px;
        letter-spacing: 0.3em;
        line-height: 1.8;
        color: ${color.text3};
    }
`;

export default function AuthStage({ title, quote, children }) {
    const sceneRef = useRef(null);
    const eye = useMemo(() => ({
        focusName: () => sceneRef.current && sceneRef.current.setOpen(0.72),
        focusSecret: () => sceneRef.current && sceneRef.current.setOpen(0),
        blur: () => sceneRef.current && sceneRef.current.setOpen(null),
    }), []);

    return (
        <Stage>
            <Layer style={{ zIndex: 1 }}>
                <SceneCanvas ref={sceneRef} mode="watcher" flesh style={{ filter: 'brightness(0.62)' }} />
            </Layer>
            <Vignette />
            <Halftone />
            <Column>
                <CornerCard pad="40px 44px" bg="rgba(15, 5, 9, 0.86)" style={{ backdropFilter: 'blur(6px)' }}>
                    <Head>
                        <h1>{title}</h1>
                        {quote && <p>「{quote}」</p>}
                    </Head>
                    {typeof children === 'function' ? children(eye) : children}
                </CornerCard>
            </Column>
        </Stage>
    );
}
